import { state } from '../../../context/store'
import {
  Button,
  Text,
  Column,
  Row,
  TextInput,
  Grid,
  Number,
  FileInput,
} from '../../Blocks'
import { ColorSwatchContainer, FontSelector } from '../index'
import { closePopups } from '../../../utilities'
import { useSnapshot } from 'valtio'
import {
  updateBadgeNumber,
  resetBadgeNumbers,
  selectBadgeNumberColor,
} from '../../../utilities/numberPadMethods'

// --- Sub-components defined outside ModalSuperBadge ---

const NameInput = ({ status, badge }) => {
  return (
    <Column id="SuperBadgeChoices">
      {status.badgeNameMode === 'name' && (
        <Column>
          <Text size="16px">Enter your rider name</Text>
          <TextInput
            label="Rider Name"
            maxLength={10}
            placeholder="Enter your message here"
            value={badge.riderName}
            changeHandler={(e) =>
              (state.current.choices.superBadge.selected.riderName = e.target.value)
            }
          />
        </Column>
      )}

      {status.badgeNameMode === 'color' && (
        <>
          <Text size="14px">Select your font style</Text>
          <FontSelector
            current={badge.nameFont}
            target="nameFont"
            sizeTarget="riderNameSize"
          />
          <Text size="14px">Select your font color</Text>
          {/* <Text size="14px">Select: {badge.nameFont}</Text>
          <Text size="14px">Select: {badge.riderNameSize}</Text> */}
          <ColorSwatchContainer limit="nopattern" section="superBadge" />
        </>
      )}
    </Column>
  )
}

const NumberInput = ({ status, badge }) => {
  return (
    <>
      {status.badgeNumberMode === 'number' && (
        <Column gap="6px">
          <Text size="16px">Enter your rider number</Text>
          <Text size="19px" weight="600">
            {badge.number}
          </Text>
          <Grid
            cols="repeat(3, 50px)"
            rows="repeat(4, 50px)"
            gap="12px"
            width="fit-content"
          >
            <Number number="1" handleClick={() => updateBadgeNumber('1')} />
            <Number number="2" handleClick={() => updateBadgeNumber('2')} />
            <Number number="3" handleClick={() => updateBadgeNumber('3')} />
            <Number number="4" handleClick={() => updateBadgeNumber('4')} />
            <Number number="5" handleClick={() => updateBadgeNumber('5')} />
            <Number number="6" handleClick={() => updateBadgeNumber('6')} />
            <Number number="7" handleClick={() => updateBadgeNumber('7')} />
            <Number number="8" handleClick={() => updateBadgeNumber('8')} />
            <Number number="9" handleClick={() => updateBadgeNumber('9')} />
            <Number number="↻" bg="#ddaaaa" handleClick={() => resetBadgeNumbers()} />
            <Number number="0" handleClick={() => updateBadgeNumber('0')} />
            <Number
              number="✔"
              bg="#aaccaa"
              handleClick={() => selectBadgeNumberColor()}
            />
          </Grid>
        </Column>
      )}

      {status.badgeNumberMode === 'color' && (
        <>
          <Text size="14px">Select your font style</Text>
          <FontSelector current={badge.numFont} target="numFont" sizeTarget="numSize" />

          <Text size="14px">Select your font color</Text>
          <ColorSwatchContainer limit="nopattern" section="superBadge" />
        </>
      )}
    </>
  )
}

const ImageInput = ({ image, imageName, handleImageUpload }) => {
  return (
    <Column justify="center" gap="6px">
      {image ? (
        <Column justify="center" gap="0">
          <img
            src={image}
            style={{ objectFit: 'cover', aspectRatio: image.ratio }}
            width="80%"
          />
          <span>{imageName}</span>
        </Column>
      ) : (
        <>
          <Text size="16px">Image must be between 1024px and 2048px</Text>
          <Text size="14px"> (width & height)</Text>
          <FileInput accent src={image} handleChange={handleImageUpload}>
            <span>Upload logo</span>
          </FileInput>
        </>
      )}
    </Column>
  )
}

// --- Main component ---

const ModalSuperBadge = () => {
  const badge = useSnapshot(state.current.choices.superBadge.selected)
  const status = useSnapshot(state.current.status)
  const image = useSnapshot(state.current).uploadedFile.image
  const imageName = useSnapshot(state.current).uploadedFile.name

  const removingBadge = (e) => {
    closePopups(e)
    state.current.status.badgeSelectMode = 'none'
    state.current.choices.superBadge.selected.subMode = 'none'
    state.current.choices.superBadge.selected.mode = 'none'
  }
  const closeBadgeModal = (e) => {
    closePopups(e)
  }

  const changeBadgeModeToColor = () => {
    state.current.status.badgeNumberMode = 'color'
    state.current.status.badgeNameMode = 'color'
  }

  const changeBadgeModeToContent = () => {
    state.current.status.badgeNumberMode = 'number'
    state.current.status.badgeNameMode = 'name'
  }

  const setBadgeMode = (value) => {
    state.current.choices.superBadge.selected.mode = 'badge'
    state.current.status.badgeSelectMode = value
    state.current.status.badgeNumberMode = 'number'
    state.current.choices.superBadge.selected.subMode = value
  }

  const changeBadgeMode = () => {
    state.current.status.badgeSelectMode = 'none'
  }

  const confirmChoice = (e) => {
    closePopups(e)
    state.current.status.badgeNumberMode = 'number'
    state.current.status.badgeNameMode = 'name'
  }

  const handleImageUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    const MIN = 500
    const MAX = 2048

    const { width, height, ratio } = await getImageRatio(file)

    const isTooSmall = width < MIN || height < MIN
    const isTooLarge = width > MAX || height > MAX

    if (isTooSmall || isTooLarge) {
      alert('Image must be between 500px and 2048px (width & height)')
      e.target.value = null
      return
    }

    const url = URL.createObjectURL(file)

    state.current.uploadedFile.image = url
    state.current.uploadedFile.name = file.name
    state.current.uploadedFile.width = width
    state.current.uploadedFile.height = height
    state.current.uploadedFile.ratio = ratio
  }

  function getImageRatio(file) {
    return new Promise((resolve) => {
      const img = new Image()
      const url = URL.createObjectURL(file)

      img.onload = () => {
        const ratio = img.width / img.height
        URL.revokeObjectURL(url)
        resolve({ width: img.width, height: img.height, ratio })
      }

      img.src = url
    })
  }

  return (
   <>
      {status.badgeSelectMode === 'none' && <Text size="22px">Pick your Option</Text>}

      {status.badgeSelectMode === 'name' && <NameInput status={status} badge={badge} />}
      {status.badgeSelectMode === 'number' && (
        <NumberInput status={status} badge={badge} />
      )}
      {status.badgeSelectMode === 'image' && (
        <ImageInput
          image={image}
          imageName={imageName}
          handleImageUpload={handleImageUpload}
        />
      )}

      {status.badgeSelectMode === 'none' && (
        <Column gap="12px">
          <Button fullWidth handleClick={() => setBadgeMode('number')}>
            with Rider Number
          </Button>
          <Button fullWidth handleClick={() => setBadgeMode('name')}>
            with Rider Name
          </Button>
          {/* <Button fullWidth handleClick={() => setBadgeMode('image')}>
            Upload Logo
          </Button> */}
        </Column>
      )}

      <Row
        justify={status.badgeSelectMode === 'none' ? 'start' : 'space-between'}
        gap="12px"
      >
        {status.badgeSelectMode === 'none' && (
          <Button handleClick={(e) => removingBadge(e)}>Remove Badge</Button>
        )}

        {status.badgeSelectMode !== 'none' && (
          <Button handleClick={() => changeBadgeMode()}>Back to Options</Button>
        )}

        {status.badgeSelectMode === 'number' && status.badgeNumberMode !== 'number' && (
          <Button handleClick={() => changeBadgeModeToContent()}>Change Number</Button>
        )}

        {status.badgeSelectMode === 'name' && status.badgeNameMode !== 'name' && (
          <Button handleClick={() => changeBadgeModeToContent()}>Change Name</Button>
        )}

        {(status.badgeSelectMode === 'number' || status.badgeSelectMode === 'name') &&
          status.badgeNameMode !== 'color' &&
          status.badgeNumberMode !== 'color' && (
            <Button handleClick={() => changeBadgeModeToColor()}>Change Styling</Button>
          )}

        {/* {status.badgeSelectMode === 'image' && image && (
          <FileInput handleChange={handleImageUpload}>
            <span>Change logo</span>
          </FileInput>
        )} */}

        {status.badgeSelectMode !== 'none' && (
          <Button accent handleClick={(e) => confirmChoice(e)}>
            Confirm
          </Button>
        )}
      </Row>
    </>
  )
}

export default ModalSuperBadge
